/**
 * One integration, as a tile.
 *
 * The page used to be a stack of full-width rows, each carrying its own form
 * and a paragraph explaining itself. Damon's note: the marks were far too
 * small, the real logos were missing, and the configuration should live behind
 * the tile rather than beside it.
 *
 * So the grid answers exactly one question - what is connected and is it
 * working - and everything else is one click away, in a modal. Status reads at
 * a glance from the badges under the mark; nothing on the grid needs reading.
 *
 * WHERE A LOGO COMES FROM, in order:
 *
 *   1. SOURCE_ICON, for the five sources that actually collect. These are
 *      curated files imported through Vite, so they are content-hashed and a
 *      corrected logo can never be served stale from cache. The sidebar has
 *      used them all along - Damon asked for the StorageCraft mark from the
 *      side menu, and this is that same file rather than a second copy of it.
 *   2. /logos/<id>, the marks fetched from each vendor's own site for the
 *      registry. Self-hosted: a logo CDN would tell a third party which backup
 *      vendors every tenant runs, on every page view.
 *   3. Initials on the vendor's brand colour.
 *
 * The third is not a placeholder waiting to be replaced - about a dozen of
 * these vendors publish nothing above a 32px favicon anywhere, and it is
 * deliberately not distinguishable as "missing", because a grid pockmarked
 * with broken-image icons reads as a broken product.
 */

import { SOURCE_ICON } from '../assets';
import type { Source } from '../types';
import { logoFor } from '../vendorLogos';

export interface TileMark {
  initials: string;
  colour: string;
  ink: string;
}

export interface TileBadge {
  text: string;
  tone: 'ok' | 'bad' | 'warn' | 'plain';
}

/** The curated mark for a collecting source, or null for everything else. */
export function sourceIcon(id: string): string | null {
  return (SOURCE_ICON as Record<string, string | undefined>)[id as Source] ?? null;
}

export function VendorTile({ id, label, mark, badges, selected, onClick }: {
  /** Matches SOURCE_ICON, or the logo filename, when we have either. */
  id: string;
  label: string;
  mark?: TileMark;
  badges: TileBadge[];
  selected?: boolean;
  onClick: () => void;
}) {
  const logo = sourceIcon(id) ?? logoFor(id);

  return (
    <button
      type="button"
      className={'vtile' + (selected ? ' on' : '')}
      onClick={onClick}
      aria-pressed={selected}
    >
      <span className="vtile-art">
        {logo ? (
          // Decorative: the label is right underneath, so announcing the logo
          // too would just read the vendor's name twice.
          <img src={logo} alt="" className="vtile-logo" loading="lazy" />
        ) : (
          <span
            className="vtile-mark"
            style={{ background: mark?.colour ?? 'var(--surface-2)', color: mark?.ink ?? 'var(--text)' }}
          >
            {mark?.initials ?? label.slice(0, 2).toUpperCase()}
          </span>
        )}
      </span>
      <span className="vtile-name">{label}</span>
      <span className="vtile-badges">
        {badges.map((b) => (
          <span key={b.text} className={'tag' + (b.tone === 'plain' ? '' : ` tag-${b.tone}`)}>
            {b.text}
          </span>
        ))}
      </span>
    </button>
  );
}
